import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatINR, formatApiError, track } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageCircle, Loader2, Tag } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

const empty = { name: "", phone: "", email: "", house: "", street: "", village: "", district: "", state: "", pincode: "", instructions: "" };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CheckoutPage() {
  const { items, lines, clear } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [coupon, setCoupon] = useState("");
  const [calc, setCalc] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [method, setMethod] = useState("razorpay");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const idemKey = useRef(crypto.randomUUID());

  const rzpEnabled = settings?.razorpay_enabled;
  const codEnabled = settings?.delivery?.cod_enabled;

  useEffect(() => {
    if (!rzpEnabled && codEnabled) setMethod("cod");
  }, [rzpEnabled, codEnabled]);

  useEffect(() => {
    if (items.length === 0) return;
    track("begin_checkout", {});
    revalidate(coupon, form.pincode);
    // eslint-disable-next-line
  }, []);

  const revalidate = async (couponCode, pincode) => {
    try {
      const { data } = await api.post("/cart/validate", { items: lines, coupon_code: couponCode || null, pincode: pincode || null });
      setCalc(data);
    } catch (e) {
      setCalc(null);
    }
  };

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "pincode" && v.length === 6) revalidate(coupon, v);
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    const { data } = await api.post("/cart/validate", { items: lines, coupon_code: coupon, pincode: form.pincode || null });
    setCalc(data);
    if (data.applied_coupon) toast.success(data.coupon_msg);
    else toast.error(data.coupon_msg || "Invalid coupon");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^([6-9]\d{9})$/.test(form.phone.replace(/\D/g, "").replace(/^91/, ""))) e.phone = "Enter valid 10-digit mobile";
    if (!form.house.trim()) e.house = "Required";
    if (!form.street.trim()) e.street = "Required";
    if (!form.village.trim()) e.village = "Required";
    if (!form.district.trim()) e.district = "Required";
    if (!form.state.trim()) e.state = "Required";
    if (!/^[1-9]\d{5}$/.test(form.pincode)) e.pincode = "Enter valid 6-digit PIN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const placeOrder = async () => {
    if (!confirm) return toast.error("Please confirm your delivery details are correct.");
    if (!validate()) return toast.error("Please fix the highlighted fields.");
    if (calc && !calc.deliverable) return toast.error(calc.pin_msg);
    setLoading(true);
    try {
      const payload = {
        items: lines,
        customer: { name: form.name, phone: form.phone, email: form.email },
        address: { house: form.house, street: form.street, village: form.village, district: form.district, state: form.state, pincode: form.pincode, instructions: form.instructions },
        coupon_code: calc?.applied_coupon || null,
        payment_method: method,
        confirm: true,
        idempotency_key: idemKey.current,
      };
      const { data } = await api.post("/checkout", payload);

      if (data.payment_method === "cod") {
        track("purchase", { order_number: data.order_number, method: "cod" });
        clear();
        navigate(`/order/${data.order_number}`);
        return;
      }

      // Razorpay
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment. Please try WhatsApp order."); setLoading(false); return; }
      track("payment_initiated", { order_number: data.order_number });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Areeba Multigrain Aata",
        description: `Order ${data.order_number}`,
        order_id: data.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#1F4D36" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              order_number: data.order_number,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            track("payment_success", { order_number: data.order_number });
            clear();
            navigate(`/order/${data.order_number}`);
          } catch (err) {
            track("payment_failure", { order_number: data.order_number });
            toast.error("Payment verification failed. If money was deducted, contact us on WhatsApp with your order number.");
            navigate(`/order/${data.order_number}`);
          }
        },
        modal: { ondismiss: () => { setLoading(false); toast("Payment cancelled. Your order is saved as pending."); } },
      });
      rzp.on("payment.failed", () => { track("payment_failure", {}); toast.error("Payment failed. Please try again."); setLoading(false); });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif text-3xl font-bold text-forest-dark">Your cart is empty</h1>
        <Button onClick={() => navigate("/")} className="mt-6 bg-forest text-cream rounded-full h-11 px-6">Shop Now</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-serif text-3xl font-bold text-forest-dark mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-forest/10 space-y-4">
            <h2 className="font-serif text-xl font-semibold text-forest-dark">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field form={form} set={set} errors={errors} id="name" label="Full Name" placeholder="Your name" />
              <Field form={form} set={set} errors={errors} id="phone" label="Mobile Number" placeholder="10-digit mobile" inputMode="numeric" />
            </div>
            <Field form={form} set={set} errors={errors} id="email" label="Email (optional)" placeholder="you@example.com" type="email" />
            <Field form={form} set={set} errors={errors} id="house" label="House / Flat Number" />
            <Field form={form} set={set} errors={errors} id="street" label="Street / Locality" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field form={form} set={set} errors={errors} id="village" label="Village / Town" />
              <Field form={form} set={set} errors={errors} id="district" label="District" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field form={form} set={set} errors={errors} id="state" label="State" />
              <Field form={form} set={set} errors={errors} id="pincode" label="PIN Code" inputMode="numeric" maxLength={6} />
            </div>
            {calc && form.pincode.length === 6 && !calc.deliverable && (
              <p className="text-sm text-discount bg-discount/10 rounded-lg p-3" data-testid="pin-error">
                {calc.pin_msg} <button onClick={() => openWhatsApp()} className="underline font-semibold">Contact on WhatsApp</button>
              </p>
            )}
            <div>
              <Label htmlFor="instructions" className="text-sm text-charcoal/80">Delivery Instructions (optional)</Label>
              <Textarea id="instructions" data-testid="checkout-instructions" value={form.instructions} onChange={set("instructions")} className="mt-1" rows={2} />
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-2xl p-6 border border-forest/10 space-y-3">
            <h2 className="font-serif text-xl font-semibold text-forest-dark">Payment Method</h2>
            {rzpEnabled && (
              <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${method === "razorpay" ? "border-forest bg-forest/5" : "border-forest/15"}`} data-testid="checkout-payment-razorpay">
                <input type="radio" checked={method === "razorpay"} onChange={() => setMethod("razorpay")} className="accent-forest" />
                <div><p className="font-semibold text-charcoal">Pay Online</p><p className="text-xs text-charcoal/60">UPI · Cards · Net Banking · Wallets (secure via Razorpay)</p></div>
              </label>
            )}
            {codEnabled && (
              <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${method === "cod" ? "border-forest bg-forest/5" : "border-forest/15"}`} data-testid="checkout-payment-cod">
                <input type="radio" checked={method === "cod"} onChange={() => setMethod("cod")} className="accent-forest" />
                <div><p className="font-semibold text-charcoal">Cash on Delivery</p><p className="text-xs text-charcoal/60">Pay when your order arrives</p></div>
              </label>
            )}
            {!rzpEnabled && (
              <p className="text-xs text-terracotta bg-wheat-pale rounded-lg p-3">Online payment is being set up. You can use Cash on Delivery or order on WhatsApp.</p>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl p-6 border border-forest/10 sticky top-24">
            <h2 className="font-serif text-xl font-semibold text-forest-dark mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-52 overflow-y-auto hide-scrollbar">
              {(calc?.items || items).map((i) => (
                <div key={i.sku} className="flex justify-between text-sm">
                  <span className="text-charcoal/75">{i.name} {i.pack_size} × {i.quantity}</span>
                  <span className="font-semibold">{formatINR((i.line_total ?? i.price * i.quantity))}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="checkout-coupon-input" className="h-10" />
              <Button onClick={applyCoupon} variant="outline" data-testid="checkout-apply-coupon" className="border-forest/30 text-forest rounded-lg h-10"><Tag size={15} className="mr-1" />Apply</Button>
            </div>

            {calc && (
              <div className="mt-4 space-y-2 text-sm border-t border-forest/10 pt-4">
                <Row l="Subtotal" v={formatINR(calc.subtotal)} />
                {calc.discount > 0 && <Row l={`Discount (${calc.applied_coupon})`} v={`- ${formatINR(calc.discount)}`} green />}
                <Row l="Delivery" v={calc.shipping === 0 ? "FREE" : formatINR(calc.shipping)} />
                <div className="flex justify-between text-base font-bold text-forest-dark pt-2 border-t border-forest/10">
                  <span>Total</span><span data-testid="checkout-total">{formatINR(calc.total)}</span>
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 mt-5 text-sm cursor-pointer">
              <Checkbox checked={confirm} onCheckedChange={setConfirm} data-testid="checkout-confirm" className="mt-0.5" />
              <span className="text-charcoal/75">I confirm that the delivery details provided are correct.</span>
            </label>

            <Button onClick={placeOrder} disabled={loading} data-testid="checkout-submit-button" className="w-full mt-4 bg-forest hover:bg-forest-dark text-cream rounded-full h-12 text-base font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : method === "cod" ? "Place Order" : `Pay ${calc ? formatINR(calc.total) : ""}`}
            </Button>
            <Button onClick={() => openWhatsApp({ items: lines, name: form.name, address: `${form.house}, ${form.street}, ${form.village}, ${form.district}, ${form.state}`, pincode: form.pincode })} variant="outline" data-testid="checkout-whatsapp-button" className="w-full mt-2 border-whatsapp text-whatsapp rounded-full h-11 font-semibold">
              <MessageCircle size={18} className="mr-2" /> Order on WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, form, set, errors, ...props }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm text-charcoal/80">{label}</Label>
      <Input id={id} data-testid={`checkout-${id}`} value={form[id]} onChange={set(id)} {...props}
        className={`mt-1 ${errors[id] ? "border-discount" : ""}`} />
      {errors[id] && <p className="text-xs text-discount mt-1">{errors[id]}</p>}
    </div>
  );
}

const Row = ({ l, v, green }) => (
  <div className="flex justify-between">
    <span className="text-charcoal/70">{l}</span>
    <span className={green ? "text-forest font-semibold" : "font-semibold"}>{v}</span>
  </div>
);
